import React from 'react';
import { FiChevronLeft, FiChevronRight, FiGrid, FiX } from 'react-icons/fi';
import { FaFlag, FaCalculator, FaTimes } from 'react-icons/fa';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import SingleChoiceQuestion from './SingleChoiceQuestion';
import TrueFalseQuestion from './TrueFalseQuestion';
import FillInTheBlankQuestion from './FillInTheBlankQuestion';
import ShortAnswerQuestion from './ShortAnswerQuestion';
import EssayQuestion from './EssayQuestion';
import MatchingQuestion from './MatchingQuestion';
import OrderingQuestion from './OrderingQuestion';
import AccountingTableQuestion from './AccountingTableQuestion';
import EnhancedCompoundQuestion from './EnhancedCompoundQuestion';
import CompoundChoiceQuestion from './CompoundChoiceQuestion';
import DropdownSelectQuestion from './DropdownSelectQuestion';
import ShowInstructions from './ShowInstructions';

const ACCAQuestionArea = ({
  questions,
  currentQuestion,
  setCurrentQuestion,
  currentQuestionData,
  answers,
  handleAnswerSelect,
  flaggedQuestions,
  toggleQuestionFlag,
  showInstructions,
  toggleInstructions,
  showCalculator,
  setShowCalculator,
  showQuestionNavigator,
  setShowQuestionNavigator,
  exam
}) => {
  // Handle multiple choice answers (multiple correct answers allowed)
  const handleMultipleChoiceAnswer = (questionId, selectedOptionIds) => {
    // For multiple choice with multiple correct answers, we store an array of selected option IDs
    if (handleAnswerSelect) {
      // If the parent component expects a different format, we can adapt here
      handleAnswerSelect(questionId, selectedOptionIds);
    }
  };

  // Handle single choice answers
  const handleSingleChoiceAnswer = (questionId, selectedOptionId) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, selectedOptionId);
    }
  };

  // Handle true/false answers
  const handleTrueFalseAnswer = (questionId, value) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, value);
    }
  };

  // Handle fill in the blank answers
  const handleFillInTheBlankAnswer = (questionId, blankIndex, value) => {
    if (handleAnswerSelect) {
      const currentAnswers = answers[questionId] || [];
      const newAnswers = [...currentAnswers];
      newAnswers[blankIndex] = value;
      handleAnswerSelect(questionId, newAnswers);
    }
  };

  // Handle short answer responses
  const handleShortAnswerChange = (questionId, value) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, value);
    }
  };

  // Handle essay responses
  const handleEssayChange = (questionId, value) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, value);
    }
  };

  // Handle matching responses
  const handleMatchingChange = (questionId, matches) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, matches);
    }
  };

  // Handle ordering responses
  const handleOrderingChange = (questionId, order) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, order);
    }
  };

  // Handle accounting table responses
  const handleAccountingTableChange = (questionId, selectedOptions) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, selectedOptions);
    }
  };

  // Handle dropdown select responses
  const handleDropdownSelectChange = (questionId, selectedAnswers) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, selectedAnswers);
    }
  };

  // Handle enhanced compound responses
  const handleEnhancedCompoundChange = (questionId, sectionAnswers) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, sectionAnswers);
    }
  };

  // Handle compound choice responses
  const handleCompoundChoiceChange = (questionId, selectedOptions) => {
    if (handleAnswerSelect) {
      handleAnswerSelect(questionId, selectedOptions);
    }
  };
  return (
    <>
      {/* ACCA Question Area - Full Screen */}
      <div className="flex-1 p-10 bg-gray-100 overflow-auto max-w-5xl mx-auto w-full no-copy">
        
        {/* Question Navigation Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="text-lg font-semibold text-gray-700">
              Question {currentQuestion + 1} of {questions.length}
            </div>
            <div className="text-sm text-gray-500">
              {currentQuestionData?.marks ? `${currentQuestionData.marks} marks` : 'No marks specified'}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className="bg-red-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Data Debug Info */}
        {/* Removed debug panel */}

        {/* Dynamic Question Content */}
        {currentQuestionData && currentQuestionData.type ? (
          <div className="bg-white rounded-lg mb-6">
            {/* Question Header */}
           

            {/* Question Text */}
            {/* <div className="text-gray-800 text-base leading-relaxed mb-6">
              {currentQuestionData.text || currentQuestionData.question || 'No question text available'}
            </div> */}

            {/* Debug Info */}
            {/* Removed debug info block */}

            {/* Dynamic Question Rendering Based on Type */}
            {currentQuestionData.type === 'SINGLE_CHOICE' && (
              <SingleChoiceQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleSingleChoiceAnswer}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'MULTIPLE_CHOICE' && (
              <MultipleChoiceQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleMultipleChoiceAnswer}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'TRUE_FALSE' && (
              <TrueFalseQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleTrueFalseAnswer}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'FILL_IN_THE_BLANK' && (
              <FillInTheBlankQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleFillInTheBlankAnswer}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'SHORT_ANSWER' && (
              <ShortAnswerQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleShortAnswerChange}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'ESSAY' && (
              <EssayQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleEssayChange}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'MATCHING' && (
              <MatchingQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleMatchingChange}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'ORDERING' && (
              <OrderingQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleOrderingChange}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'ACCOUNTING_TABLE' && (
              <AccountingTableQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleAccountingTableChange}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'ENHANCED_COMPOUND' && (
              <EnhancedCompoundQuestion
                question={currentQuestionData}
                answers={answers}
                onAnswerChange={handleEnhancedCompoundChange}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'COMPOUND_CHOICE' && (
              <CompoundChoiceQuestion
                question={currentQuestionData}
                onAnswer={handleCompoundChoiceChange}
                currentAnswer={answers[currentQuestionData.id]}
                questionIndex={currentQuestion}
              />
            )}

            {currentQuestionData.type === 'DROPDOWN_SELECT' && (
              <DropdownSelectQuestion
                question={currentQuestionData}
                onAnswer={handleDropdownSelectChange}
                currentAnswer={answers[currentQuestionData.id]}
                questionIndex={currentQuestion}
              />
            )}

            {/* Fallback for unknown question types */}
            {!currentQuestionData.type && (
              <div className="text-gray-600 text-center py-8">
                <p>Question type not specified. Please contact support.</p>
              </div>
            )}

            {/* Fallback for when no question type matches the conditions */}
            {currentQuestionData.type && 
             currentQuestionData.type !== 'SINGLE_CHOICE' &&
             currentQuestionData.type !== 'MULTIPLE_CHOICE' &&
             currentQuestionData.type !== 'TRUE_FALSE' &&
             currentQuestionData.type !== 'FILL_IN_THE_BLANK' &&
             currentQuestionData.type !== 'SHORT_ANSWER' &&
             currentQuestionData.type !== 'ESSAY' &&
             currentQuestionData.type !== 'MATCHING' &&
             currentQuestionData.type !== 'ORDERING' &&
             currentQuestionData.type !== 'ACCOUNTING_TABLE' &&
             currentQuestionData.type !== 'ENHANCED_COMPOUND' &&
             currentQuestionData.type !== 'COMPOUND_CHOICE' &&
             currentQuestionData.type !== 'DROPDOWN_SELECT' && (
              <div className="bg-red-50 border border-red-200 rounded p-4 text-center">
                <p className="text-red-800 font-semibold">Question Type Not Supported</p>
                <p className="text-red-600 text-sm">Type: {currentQuestionData.type}</p>
                <p className="text-red-600 text-sm">This question type is not yet implemented.</p>
              </div>
            )}

            {/* Debug: Show which question types are being rendered */}
            {/* Removed debug line */}
          </div>
        ) : (
          /* No Questions State - Show minimal message */
          questions.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <div className="text-gray-500">
                <p>No questions available for this exam.</p>
              </div>
            </div>
          )
        )}


      </div>

      {/* ACCA Instructions Modal */}
      {showInstructions && (
        <ShowInstructions
          exam={exam}
          onNext={toggleInstructions}
          onExit={toggleInstructions}
        />
      )}

      {/* ACCA Calculator Modal */}
      {showCalculator && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-3 border-red-600 rounded-none p-5 z-50 min-w-[300px] shadow-2xl">
          <div className="flex justify-between items-center mb-5 border-b-2 border-red-600 pb-3">
            <h3 className="text-xl font-bold text-red-600 m-0 flex items-center gap-2">
              <FaCalculator size={20} />
              🧮 ACCA CALCULATOR
            </h3>
            <button
              onClick={() => setShowCalculator(false)}
              className="bg-red-600 text-white border-none w-8 h-8 rounded-full text-base cursor-pointer font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="text-center p-5">
            <p className="text-gray-600 mb-5">
              Scientific calculator functionality will be implemented here.
            </p>
            <div className="bg-gray-50 border border-gray-300 p-4 rounded font-mono text-lg min-h-[50px] flex items-center justify-center">
              Calculator Display
            </div>
          </div>
        </div>
      )}

      {/* ACCA Question Navigator Modal */}
      {showQuestionNavigator && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-3 border-red-600 rounded-none p-5 z-50 min-w-[400px] max-h-[80vh] overflow-auto shadow-2xl">
          <div className="flex justify-between items-center mb-5 border-b-2 border-red-600 pb-3">
            <h3 className="text-xl font-bold text-red-600 m-0 flex items-center gap-2">
              <FiGrid size={20} />
              🧭 ACCA QUESTION NAVIGATOR
            </h3>
            <button
              onClick={() => setShowQuestionNavigator(false)}
              className="bg-red-600 text-white border-none w-8 h-8 rounded-full text-base cursor-pointer font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))] gap-2.5 p-5">
            {questions.map((question, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentQuestion(index);
                  setShowQuestionNavigator(false);
                }}
                className={`p-2.5 text-sm font-semibold cursor-pointer min-h-[50px] flex items-center justify-center relative rounded border-2 transition-colors ${
                  currentQuestion === index 
                    ? 'bg-red-600 text-white border-red-700' 
                    : answers[question.id] 
                      ? 'bg-green-500 text-white border-green-600'
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                }`}
              >
                {index + 1}
                {flaggedQuestions.has(index) && (
                  <FaFlag 
                    size={12} 
                    className={`absolute top-0.5 right-0.5 ${
                      currentQuestion === index ? 'text-white' : 'text-red-600'
                    }`}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ACCAQuestionArea;
